import { Button, Form, Modal, Select } from "semantic-ui-react";
import { initParameter, IParameter, NetworkCode, networkCodeOptions } from "../../models/parameter";
import SemanticDatepicker from "react-semantic-ui-datepickers";
import { SemanticDatepickerProps } from "react-semantic-ui-datepickers/dist/types";
import "./ParameterModal.css";
import { useCallback, useEffect, useState } from "react";
import { toast } from "../..";
import { createParameter, editParameter, getParameter } from "../../services/parametersService";
import { EVisibility } from "../../models/form";

const submitIsDisabled = (parameter: IParameter) => {
    return (
        parameter.networkCode === NetworkCode.Unknown ||
        parameter.name === "" ||
        parameter.answer1text === "" ||
        parameter.answer2text === "" ||
        parameter.answer3text === "" ||
        parameter.answer4text === "" ||
        parameter.answer5text === "" ||
        parameter.answer6text === "" ||
        parameter.answer7text === ""
    );
};

interface IProps {
    modalOpen: boolean;
    closeModal: () => void;
    parameterGuid?: string;
    refreshTrigger?: () => void;
    isDevelopment: boolean;
}

export const ParameterModal = ({
    modalOpen,
    closeModal,
    parameterGuid,
    refreshTrigger,
    isDevelopment,
}: IProps) => {
    const [parameter, setParameter] = useState<IParameter>(initParameter);
    const [loading, setLoading] = useState(false);

    const fetchParameter = useCallback(async () => {
        if (!parameterGuid) {
            return;
        }

        try {
            setLoading(true);
            const res = await getParameter(parameterGuid);
            setParameter(res);
        } catch {
            toast("Error fetching parameter", false, 1000);
        }
        setLoading(false);
    }, [parameterGuid]);

    useEffect(() => {
        fetchParameter();
    }, [fetchParameter]);

    const setDefaultAnswers = () => {
        setParameter({
            ...parameter,
            answer1text: initParameter.answer1text,
            answer2text: initParameter.answer2text,
            answer3text: initParameter.answer3text,
            answer4text: initParameter.answer4text,
            answer5text: initParameter.answer5text,
            answer6text: initParameter.answer6text,
            answer7text: initParameter.answer7text,
        });
    };

    const addParameter = async () => {
        try {
            setLoading(true);
            parameter.visibility = EVisibility.Internal;
            parameter.year = new Date().getFullYear().toString();
            await createParameter(parameter);
            closeModal();
            setParameter(initParameter);
            refreshTrigger && refreshTrigger();
        } catch {
            toast("Error creating parameter", false, 1000);
        }
        setLoading(false);
    };

    const updateParameter = async () => {
        if (!parameterGuid) {
            return;
        }
        try {
            setLoading(true);
            await editParameter(parameterGuid, parameter);
            closeModal();
            setParameter(initParameter);
            refreshTrigger && refreshTrigger();
        } catch {
            toast("Error updating parameter", false, 1000);
        }
        setLoading(false);
    };

    return (
        <Modal
            className="parameter-modal"
            open={modalOpen}
            onClose={() => {
                setParameter(initParameter);
                closeModal();
            }}
            size="small"
            closeOnDimmerClick={false}
        >
            <Modal.Header>{parameterGuid ? "Edit parameter" : "Add new parameter"}</Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Field>
                        <label>Name</label>
                        <input
                            value={parameter?.name}
                            onChange={e =>
                                setParameter({
                                    ...parameter,
                                    name: e.target.value,
                                })
                            }
                            disabled={loading || !isDevelopment}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Network code</label>
                        <Select
                            fluid
                            clearable
                            value={parameter?.networkCode}
                            options={networkCodeOptions}
                            onChange={(_e, data) => {
                                setParameter({
                                    ...parameter,
                                    networkCode:
                                        data.value === ""
                                            ? NetworkCode.Unknown
                                            : (data.value as NetworkCode),
                                });
                            }}
                            selectOnBlur={false}
                            disabled={loading || !isDevelopment}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Deadline</label>
                        <SemanticDatepicker
                            format="DD.MM.YYYY."
                            value={
                                parameter?.deadline === null ||
                                parameter?.deadline === undefined ||
                                parameter?.deadline === "" ||
                                parameter?.deadline === "01.01.0001. 00:00:00" ||
                                parameter?.deadline.startsWith("190")
                                    ? null
                                    : new Date(parameter?.deadline)
                            }
                            onChange={(
                                event: React.SyntheticEvent<Element, Event> | undefined,
                                data: SemanticDatepickerProps
                            ) => {
                                if (event !== undefined) {
                                    let utcDate = undefined;
                                    if (data.value != null) {
                                        utcDate = `${(data.value as Date).getFullYear()}-${
                                            (data.value as Date).getMonth() + 1
                                        }-${(data.value as Date).getDate()}`;

                                        setParameter({
                                            ...parameter,
                                            deadline: utcDate,
                                        });
                                    } else {
                                        setParameter({
                                            ...parameter,
                                            deadline: "",
                                        });
                                    }
                                }
                            }}
                            disabled={loading}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Answer 1</label>
                        <div className="answer-container">
                            <input
                                className="answer"
                                value={parameter?.answer1text}
                                onChange={e =>
                                    setParameter({
                                        ...parameter,
                                        answer1text: e.target.value,
                                    })
                                }
                                disabled={loading || !isDevelopment}
                            />
                            <div className="color-rectangle answer1"></div>
                        </div>
                    </Form.Field>
                    <Form.Field>
                        <label>Answer 2</label>
                        <div className="answer-container">
                            <input
                                className="answer answer2"
                                value={parameter?.answer2text}
                                onChange={e =>
                                    setParameter({
                                        ...parameter,
                                        answer2text: e.target.value,
                                    })
                                }
                                disabled={loading || !isDevelopment}
                            />
                            <div className="color-rectangle answer2"></div>
                        </div>
                    </Form.Field>
                    <Form.Field>
                        <label>Answer 3</label>
                        <div className="answer-container">
                            <input
                                className="answer"
                                value={parameter?.answer3text}
                                onChange={e =>
                                    setParameter({
                                        ...parameter,
                                        answer3text: e.target.value,
                                    })
                                }
                                disabled={loading || !isDevelopment}
                            />
                            <div className="color-rectangle answer3"></div>
                        </div>
                    </Form.Field>
                    <Form.Field>
                        <label>Answer 4</label>
                        <div className="answer-container">
                            <input
                                className="answer"
                                value={parameter?.answer4text}
                                onChange={e =>
                                    setParameter({
                                        ...parameter,
                                        answer4text: e.target.value,
                                    })
                                }
                                disabled={loading || !isDevelopment}
                            />
                            <div className="color-rectangle answer4"></div>
                        </div>
                    </Form.Field>
                    <Form.Field>
                        <label>Answer 5</label>
                        <div className="answer-container">
                            <input
                                className="answer"
                                value={parameter?.answer5text}
                                onChange={e =>
                                    setParameter({
                                        ...parameter,
                                        answer5text: e.target.value,
                                    })
                                }
                                disabled={loading || !isDevelopment}
                            />
                            <div className="color-rectangle answer5"></div>
                        </div>
                    </Form.Field>
                    <Form.Field>
                        <label>Answer 6</label>
                        <div className="answer-container">
                            <input
                                className="answer"
                                value={parameter?.answer6text}
                                onChange={e =>
                                    setParameter({
                                        ...parameter,
                                        answer6text: e.target.value,
                                    })
                                }
                                disabled={loading || !isDevelopment}
                            />
                            <div className="color-rectangle answer6"></div>
                        </div>
                    </Form.Field>
                    <Form.Field>
                        <label>Answer 7</label>
                        <div className="answer-container">
                            <input
                                className="answer"
                                value={parameter?.answer7text}
                                onChange={e =>
                                    setParameter({
                                        ...parameter,
                                        answer7text: e.target.value,
                                    })
                                }
                                disabled={loading || !isDevelopment}
                            />
                            <div className="color-rectangle answer7"></div>
                        </div>
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    primary
                    onClick={() => {
                        setDefaultAnswers();
                    }}
                    content="Set default answers"
                    disabled={loading || !isDevelopment}
                />
                <div className="basic-actions">
                    <Button
                        onClick={() => {
                            setParameter(initParameter);
                            closeModal();
                        }}
                        content="Cancel"
                    />
                    <Button
                        primary
                        onClick={parameterGuid ? updateParameter : addParameter}
                        disabled={submitIsDisabled(parameter)}
                        content={parameterGuid ? "Update" : "Add"}
                    />
                </div>
            </Modal.Actions>
        </Modal>
    );
};
